import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { authApi } from '../services/api';

interface User {
  id: string;
  email: string;
  name: string;
  roles: string[];
  groups: string[];
  photo?: string;
}

// Admin group ID for restricted access (Azure AD group: GRP_SECRET-ADMIN)
export const ADMIN_GROUP_ID = 'bcd972f6-03b3-4061-8b4a-f27c8ef7cd82';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: User | null;
  isAdmin: boolean;
  graphAccessToken: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  devLogin: (email?: string, name?: string) => Promise<void>;
  handleCallback: (code: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// PKCE helper functions
function generateRandomString(length: number): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
}

async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode(...new Uint8Array(hash)));
  // Convert to base64url
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Fetch user photo from Microsoft Graph
async function fetchUserPhoto(accessToken: string): Promise<string | undefined> {
  try {
    const response = await fetch('https://graph.microsoft.com/v1.0/me/photo/$value', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      return undefined;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch {
    return undefined;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [graphAccessToken, setGraphAccessToken] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      const storedGraphToken = localStorage.getItem('graphAccessToken');

      if (token && storedUser) {
        try {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
          if (storedGraphToken) {
            setGraphAccessToken(storedGraphToken);
          }
        } catch {
          // Invalid stored data, clear it
          localStorage.removeItem('authToken');
          localStorage.removeItem('idToken');
          localStorage.removeItem('graphAccessToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const login = useCallback(async () => {
    // Get Azure AD config and redirect using Authorization Code flow with PKCE
    try {
      const configResponse = await authApi.getConfig();
      const { authority, clientId, redirectUri, scopes } = configResponse.data;

      // Generate PKCE code verifier and challenge
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);

      // Store code verifier for token exchange
      sessionStorage.setItem('pkce_code_verifier', codeVerifier);
      console.log('[Auth] Code verifier stored:', codeVerifier.substring(0, 10) + '...');
      console.log('[Auth] Code challenge:', codeChallenge);

      // Generate state for CSRF protection
      const state = generateRandomString(32);
      sessionStorage.setItem('auth_state', state);

      // Redirect to Azure AD login (Authorization Code flow with PKCE)
      const params = new URLSearchParams({
        client_id: clientId,
        response_type: 'code',
        redirect_uri: redirectUri,
        scope: scopes.join(' '),
        response_mode: 'query',
        state: state,
        code_challenge: codeChallenge,
        code_challenge_method: 'S256',
      });

      window.location.href = `${authority}/oauth2/v2.0/authorize?${params.toString()}`;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  }, []);

  const handleCallback = useCallback(async (code: string) => {
    try {
      // Get config and code verifier
      const configResponse = await authApi.getConfig();
      const { authority, clientId, redirectUri } = configResponse.data;

      const codeVerifier = sessionStorage.getItem('pkce_code_verifier');
      console.log('[Auth] Code verifier retrieved:', codeVerifier ? codeVerifier.substring(0, 10) + '...' : 'NOT FOUND');
      console.log('[Auth] Authorization code:', code.substring(0, 20) + '...');

      if (!codeVerifier) {
        throw new Error('Code verifier not found. Please try logging in again.');
      }

      // Exchange code for tokens directly with Azure AD (SPA flow)
      // Include Mail.Send for email functionality
      const tokenResponse = await fetch(`${authority}/oauth2/v2.0/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          client_id: clientId,
          grant_type: 'authorization_code',
          code: code,
          redirect_uri: redirectUri,
          code_verifier: codeVerifier,
          scope: 'openid profile email User.Read Mail.Send',
        }),
      });

      const tokens = await tokenResponse.json();

      if (!tokenResponse.ok) {
        console.error('[Auth] Token exchange failed:', tokens);
        throw new Error(tokens.error_description || tokens.error || 'Token exchange failed');
      }

      // Store tokens
      localStorage.setItem('authToken', tokens.access_token);
      localStorage.setItem('graphAccessToken', tokens.access_token);
      setGraphAccessToken(tokens.access_token);
      if (tokens.id_token) {
        localStorage.setItem('idToken', tokens.id_token);
      }

      // Clean up session storage
      sessionStorage.removeItem('pkce_code_verifier');
      sessionStorage.removeItem('auth_state');

      // Decode the ID token to get user info
      if (tokens.id_token) {
        const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));

        // Debug: log the full token payload to see available claims
        console.log('[Auth] Token payload:', payload);
        console.log('[Auth] Groups in token:', payload.groups);
        console.log('[Auth] Roles in token:', payload.roles);

        // Fetch user photo from Microsoft Graph
        const photo = await fetchUserPhoto(tokens.access_token);

        // Groups can be in 'groups' claim (IDs) or '_claim_names' for large group sets
        const groups: string[] = payload.groups || [];

        const userData = {
          id: payload.oid || payload.sub,
          email: payload.preferred_username || payload.email || '',
          name: payload.name || '',
          roles: payload.roles || [],
          groups,
          photo,
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }

      setIsAuthenticated(true);
    } catch (error) {
      console.error('Token exchange failed:', error);
      throw error;
    }
  }, []);

  const devLogin = useCallback(async (email?: string, name?: string) => {
    try {
      const response = await authApi.devLogin(email, name);
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Dev login failed:', error);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout API errors
    }

    localStorage.removeItem('authToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('graphAccessToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('auth_state');
    setUser(null);
    setGraphAccessToken(null);
    setIsAuthenticated(false);
  }, []);

  // Check if user is in admin group (by group ID)
  const isAdmin = user?.groups?.includes(ADMIN_GROUP_ID) || user?.roles?.includes('admin') || false;

  // Debug: log admin status
  console.log('[Auth] User groups:', user?.groups);
  console.log('[Auth] Looking for group ID:', ADMIN_GROUP_ID);
  console.log('[Auth] isAdmin:', isAdmin);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        user,
        isAdmin,
        graphAccessToken,
        login,
        logout,
        devLogin,
        handleCallback,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
